import TinyColor from "@ctrl/tinycolor";
import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import classNames from "classnames";
import { KeyboardEvent, memo, useEffect, useRef, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoBadge, RedoBadgeSize } from "../badge/redo-badge";
import {
  BrandKitColor,
  RedoColorPicker,
  formatColorOutput,
} from "./redo-color-picker";
import * as css from "./redo-color-picker.module.css";

export type RedoColorPickerVariant = "badge" | "swatch";
export type RedoColorPickerSwatchStyle = "fill" | "ring";

/**
 * Module-scoped set of open-picker close functions. Whenever a picker
 * opens it closes every other open picker so only one popover is ever
 * visible at a time. ClickAwayListener handles outside clicks, but the
 * trigger calls event.stopPropagation() which prevents the document-level
 * outside-click handler of *other* open pickers from firing — so we
 * coordinate explicitly.
 */
const openPickerClosers = new Set<() => void>();

export const RedoColorPickerBadge = memo(function RedoColorPickerBadge({
  value,
  onChange,
  disabled = false,
  label,
  showAlpha = true,
  overlayPicker,
  size = "sm",
  brandKitColors,
  variant = "badge",
  swatchStyle = "fill",
  ariaLabel,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
  label?: string;
  showAlpha?: boolean;
  overlayPicker?: { shouldUse: boolean; anchorOverride?: HTMLElement | null };
  size?: RedoBadgeSize;
  brandKitColors?: BrandKitColor[];
  /**
   * "badge" (default): full badge with hex text — fits inspectors/forms.
   * "swatch": compact color circle trigger — fits dense toolbars where
   * the hex text would stretch the bar.
   */
  variant?: RedoColorPickerVariant;
  /**
   * Only used when variant="swatch". "fill" renders a solid disc; "ring"
   * renders an outlined circle (color as the border) so a stroke/border
   * swatch reads differently than a paired fill swatch.
   */
  swatchStyle?: RedoColorPickerSwatchStyle;
  /**
   * Accessible label for the swatch trigger. Recommended when variant
   * "swatch" is used since there is no visible text.
   */
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLElement>(null);

  const [open, setOpen] = useState(false);

  // When we open, close any other picker that was already open. When we
  // close (or unmount), drop ourselves from the registry.
  useEffect(() => {
    if (!open) {
      return;
    }
    const close = () => setOpen(false);
    for (const other of openPickerClosers) {
      other();
    }
    openPickerClosers.add(close);
    return () => {
      openPickerClosers.delete(close);
    };
  }, [open]);

  const handleClick = (event: { stopPropagation(): void }) => {
    if (disabled) {
      setOpen(false);
      return;
    }
    setOpen(!open);
    event.stopPropagation();
  };

  const onClickAway = useHandler(() => {
    setOpen(false);
  });

  const handleKeyDown = useHandler((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      anchorRef.current?.focus();
    }
  });

  //convert "transparent" to hex with preserved RGB values
  const normalizedValue =
    value === "transparent" && showAlpha
      ? (() => {
          // try to get previous RGB  or default to black
          const previousColor = TinyColor("#000000");
          const rgb = previousColor.toRgb();
          return TinyColor({
            r: rgb.r,
            g: rgb.g,
            b: rgb.b,
            a: 0,
          }).toHex8String();
        })()
      : value;

  // Treat a value as transparent if it's the literal "transparent" keyword
  // OR any parseable color with alpha 0 (e.g. "#00000000"). The picker
  // emits the latter, so without the alpha check a transparent border
  // would render as an invisible 4px ring with no "no color" indicator.
  const parsedColor = TinyColor(value);
  const isTransparent =
    value === "transparent" ||
    (parsedColor.isValid && parsedColor.getAlpha() === 0);

  // The swatch variant always pops out (it has no inline expanded state to
  // collide with). Honor the explicit shouldUse override otherwise.
  const usePopper = variant === "swatch" || !!overlayPicker?.shouldUse;

  const trigger =
    variant === "swatch" ? (
      <button
        aria-label={ariaLabel ?? t("Color picker", { context: "color picker" })}
        className={classNames(
          css.swatch,
          swatchStyle === "ring" && css.swatchRing,
          isTransparent && css.swatchTransparent,
          disabled && css.swatchDisabled,
        )}
        data-dd-action-name={actionName}
        disabled={disabled}
        onClick={handleClick}
        style={
          isTransparent
            ? undefined
            : swatchStyle === "ring"
              ? { borderColor: value }
              : { backgroundColor: value }
        }
        type="button"
      />
    ) : (
      <RedoBadge
        color="gray"
        data-dd-action-name={actionName}
        monospace
        segmentLeading={
          isTransparent ? undefined : { type: "color", color: value }
        }
        size={size}
        text={
          isTransparent
            ? "TRANSPARENT"
            : (() => {
                const color = TinyColor(value);
                return color.isValid
                  ? formatColorOutput(color, showAlpha).toUpperCase()
                  : value.toUpperCase();
              })()
        }
      />
    );

  return (
    <Flex
      data-arbiter="RedoColorPickerBadge"
      data-dd-action-name={actionName}
      dir="column"
      gap="xs"
    >
      {label && <Text fontSize="xs">{label}</Text>}
      <span
        className={css.badge}
        onBlur={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const relatedTarget = e.relatedTarget as HTMLElement | null;
          // If relatedTarget is null (clicking non-focusable element), let ClickAwayListener handle it
          // Only close if focus is explicitly moving to something outside the picker
          if (
            !relatedTarget ||
            relatedTarget.closest("[data-redo-color-picker]")
          ) {
            return;
          }
          setOpen(false);
        }}
        onClick={variant === "swatch" ? undefined : handleClick}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.preventDefault();
            setOpen(false);
          } else if (
            variant !== "swatch" &&
            (e.key === "Enter" || e.key === " ")
          ) {
            e.preventDefault();
            handleClick(e);
          }
        }}
        ref={anchorRef}
        tabIndex={variant === "swatch" || disabled ? -1 : 0}
      >
        {trigger}
      </span>
      {open && (
        <ClickAwayListener onClickAway={onClickAway}>
          {usePopper ? (
            <Popper
              anchorEl={overlayPicker?.anchorOverride ?? anchorRef.current}
              open={open}
            >
              <div data-redo-color-picker onKeyDown={handleKeyDown}>
                <RedoColorPicker
                  brandKitColors={brandKitColors}
                  onChange={onChange}
                  showAlpha={showAlpha}
                  value={normalizedValue}
                />
              </div>
            </Popper>
          ) : (
            <div data-redo-color-picker onKeyDown={handleKeyDown}>
              <RedoColorPicker
                brandKitColors={brandKitColors}
                onChange={onChange}
                showAlpha={showAlpha}
                value={normalizedValue}
              />
            </div>
          )}
        </ClickAwayListener>
      )}
    </Flex>
  );
});
